import type { Task } from '@dyspatch/core';
import { Link } from '@tanstack/react-router';
import { RepoChip, TaskKey } from '@/components/common/bits';
import { TaskStateChip } from '@/components/common/StatusIcon';
import { PausedChip } from '@/features/actions/PauseActions';
import { useLaneFlip } from '@/hooks/use-lane-flip';
import type { RowProps } from '@/hooks/use-row-keys';
import { holdNeedsAPerson, taskNote } from '@/lib/fleet';
import { duration, minutesSince } from '@/lib/format';
import type { TaskPageGroup } from '@/lib/task-groups';
import { cn } from '@/lib/utils';
import type { JobsSearch } from '@/router';

/**
 * The Steps page as lanes rather than as five stacked lists.
 *
 * The lanes are the states a step can be in, and they were already the page's
 * groups; what they were not was side by side. Stacked, the one lane that needs
 * a person sat above a queue of thirty, so the page was a scroll whose first
 * screen was mostly the queue's first eight rows and whose last screen was
 * history. Nothing about the fleet was ever visible at once, which is the one
 * thing a board is for.
 *
 * Two consequences worth stating, because they are the design rather than side
 * effects of it:
 *
 *   - **The page does not scroll; the lanes do.** The board takes the height it
 *     is given and each lane scrolls inside it, so the headings and the counts
 *     stay put and a queue of thirty cannot push `Closed` off the bottom of the
 *     world. This is the one place in the console where a nested scroller is
 *     right, and it is right for the reason the job page's graph scroller is:
 *     the alternative loses the frame the content is read in.
 *   - **A card that changes lane slides there** (`use-lane-flip.ts`). Approving,
 *     sending back and cancelling all move a step from one lane to another, and
 *     the motion is the receipt for the press. It is the same optimistic cache
 *     edit the list always made; what is new is that you can see it happen.
 *
 * Below `lg` the page keeps its stacked list. A five-lane board on a phone is
 * five columns of one card, which is worse than the list at every width where
 * it would fit.
 */

/** No lane is allowed to be so narrow that a title truncates to three words. */
const LANE_MIN = 'min-w-[15rem]';

function LaneHead({
  label,
  count,
  gate,
}: {
  label: string;
  count: number;
  /** The one lane that is a demand rather than a state, tinted like every other gate. */
  gate: boolean;
}) {
  return (
    <div className="flex items-baseline gap-2 px-1 pb-2">
      <h2 className={cn('text-sm font-medium', gate && count > 0 ? 'text-gate' : 'text-fg-2')}>
        {label}
      </h2>
      {count > 0 ? <span className="text-fg-3 tnum text-xs">{count}</span> : null}
    </div>
  );
}

/**
 * One step, as a card.
 *
 * Denser than the row it replaces, and it can be: a lane is one state, so the
 * state word every row used to carry is the heading above it. What is left is
 * what differs between two cards in the same lane, which is the step, the repo,
 * and how long it has been like this.
 *
 * The hold note stays, and only for a hold somebody has to clear. A step waiting
 * for a worker to come online is the fleet's state rather than this step's, and
 * on a queue of thirty it was the same sentence thirty times.
 */
function StepCard({
  task,
  selected,
  rowProps,
  flipRef,
}: {
  task: Task;
  selected: boolean;
  rowProps: RowProps;
  flipRef: (node: HTMLElement | null) => void;
}) {
  const isGate = task.status === 'review';
  const stuck = task.status === 'queued' && (holdNeedsAPerson(task) || Boolean(task.pausedAt));
  const closed = task.status === 'done' || task.status === 'cancelled';
  const note = stuck ? taskNote(task) : null;

  return (
    <li ref={flipRef}>
      <Link
        to="/jobs"
        {...rowProps}
        // A card opens the peek beside the board rather than leaving for the
        // task's own page (issue #61); the peek's own header links on to the
        // page. The selection keeps the filters that found it, so the board
        // behind the pane does not silently become a different board.
        search={(previous: JobsSearch) => ({ ...previous, task: task.id })}
        // Reading, not navigating: Back leaves the board rather than walking
        // out through every task glanced at on the way here.
        replace
        className={cn(
          'border-line bg-card relative block rounded-md border p-2.5',
          'hover:border-fg-3/40 transition-colors duration-100',
          selected && 'border-accent bg-sel',
          stuck && !selected && 'bg-warn-bg/25',
          closed && 'opacity-60',
        )}
      >
        {isGate ? (
          <span className="bg-gate absolute inset-y-1.5 left-0 w-[3px] rounded-r" aria-hidden />
        ) : null}
        <div className="flex min-w-0 items-center gap-2">
          {/* The shared state chip (issue #64), not a bare icon: a lane can
              hold more than one status (`Queued` is queued, offered, claimed
              and draft together), so the word still has to tell two cards in
              the same lane apart, not just the shape (issue #100). */}
          <TaskStateChip status={task.status} />
          <TaskKey briefId={task.briefId}>{task.key}</TaskKey>
          <span className="text-fg-3 tnum ml-auto shrink-0 text-2xs">
            {duration(minutesSince(task.updatedAt))}
          </span>
        </div>
        <p className="mt-1 line-clamp-2 text-sm" title={task.title}>
          {task.title}
        </p>
        <div className="mt-1.5 flex min-w-0 items-center gap-2">
          <RepoChip repo={task.repo} />
          {task.pausedAt ? <PausedChip /> : null}
          {task.assignedWorkerName ? (
            <span className="text-fg-3 min-w-0 truncate text-2xs">{task.assignedWorkerName}</span>
          ) : null}
        </div>
        {note ? <p className="text-warn mt-1 line-clamp-2 text-2xs">{note}</p> : null}
      </Link>
    </li>
  );
}

export function StepBoard({
  groups,
  selectedId,
  rowProps,
  className,
}: {
  groups: readonly TaskPageGroup[];
  selectedId?: string;
  rowProps: (id: string) => RowProps;
  className?: string;
}) {
  const flip = useLaneFlip();

  return (
    <div
      className={cn(
        // One row of lanes, each its own scroller, inside whatever height the
        // page hands down. `min-h-0` on both the track and the lane is what
        // lets the inner overflow actually take effect in a flex column.
        'flex min-h-0 flex-1 gap-3 overflow-x-auto pb-1',
        className,
      )}
    >
      {groups.map((group) => (
        <section
          key={group.key}
          className={cn('flex min-h-0 flex-1 shrink-0 flex-col', LANE_MIN)}
          aria-label={`${group.label}, ${group.tasks.length} task${group.tasks.length === 1 ? '' : 's'}`}
        >
          <LaneHead label={group.label} count={group.tasks.length} gate={group.key === 'gate'} />
          <div className="bg-sunken/60 border-line min-h-0 flex-1 overflow-y-auto rounded-lg border p-1.5">
            {group.tasks.length ? (
              <ul className="flex flex-col gap-1.5">
                {group.tasks.map((task) => (
                  <StepCard
                    key={task.id}
                    task={task}
                    selected={selectedId === task.id}
                    rowProps={rowProps(task.id)}
                    flipRef={flip.card(task.id)}
                  />
                ))}
              </ul>
            ) : (
              // A lane with nothing in it still holds its column, because an
              // empty `Running` is a fact about the fleet and a board that drops
              // it reflows every other lane on the next dispatch.
              <p className="text-fg-3 px-2 py-6 text-center text-xs">{group.empty}</p>
            )}
          </div>
        </section>
      ))}
    </div>
  );
}
